import { useState, useCallback, useEffect } from 'react';
import api from '../api/axios';

/**
 * useAdManager - Hook for managing ad display
 * 
 * CRITICAL: VIP users, admins, and internal testers MUST NEVER see ads.
 * 
 * Rules:
 * - VIP users NEVER see ads (is_vip = true)
 * - Admin users NEVER see ads (is_admin = true)
 * - Internal testers NEVER see ads (is_internal_tester = true)
 * - Shows ad after every N successful translations (default: 10)
 * - Capped at M ads per session (default: 3)
 * - Respects feature flags from env
 */
const useAdManager = () => {
  // Feature flags from environment
  const adsEnabled = process.env.REACT_APP_ADS_ENABLED === 'true';
  const adsMode = process.env.REACT_APP_ADS_MODE || 'house';
  const triggerCount = parseInt(process.env.REACT_APP_AD_TRIGGER_COUNT || '10', 10);
  const sessionCap = parseInt(process.env.REACT_APP_ADS_SESSION_CAP || '3', 10);

  // State - CRITICAL: All premium flags must be checked
  const [showAd, setShowAd] = useState(false);
  const [isVipUser, setIsVipUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInternalTester, setIsInternalTester] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  
  // Computed: Should this user NEVER see ads?
  const isPremiumUser = isVipUser || isAdmin || isInternalTester;
  
  // Check user status on mount - USES /api/user/profile as SOURCE OF TRUTH
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Use /api/user/profile as the SINGLE SOURCE OF TRUTH
          const response = await api.get('/user/profile');
          const data = response.data;
          
          // Set ALL premium status flags
          setIsVipUser(data.is_vip === true);
          setIsAdmin(data.is_admin === true);
          setIsInternalTester(data.is_internal_tester === true);
          
          // Debug logging for verification
          console.log('[AdManager] User status loaded:', {
            is_vip: data.is_vip,
            is_admin: data.is_admin,
            is_internal_tester: data.is_internal_tester,
            shouldShowAds: !(data.is_vip || data.is_admin || data.is_internal_tester)
          });
        }
      } catch (error) {
        // If check fails, assume not premium (safe default - will show ads)
        console.log('[AdManager] User status check failed:', error.message);
        setIsVipUser(false);
        setIsAdmin(false);
        setIsInternalTester(false);
      } finally {
        setSubscriptionChecked(true);
      }
    };
    
    checkUserStatus();
  }, []);
  
  // Get translation count from localStorage
  const getTranslationCount = useCallback(() => {
    return parseInt(localStorage.getItem('passportTalk_translationCount') || '0', 10);
  }, []);

  // Get ads shown this session from sessionStorage
  const getAdsShownThisSession = useCallback(() => {
    return parseInt(sessionStorage.getItem('passportTalk_adsShown') || '0', 10);
  }, []);

  // Increment translation count and check if ad should show
  const recordTranslation = useCallback(() => {
    // CRITICAL: Premium users NEVER see ads
    if (isPremiumUser) {
      console.log('[AdManager] Ad blocked - premium user');
      return false;
    }
    
    if (!adsEnabled || adsMode !== 'house') {
      return false;
    }

    // Check session cap
    const adsShown = getAdsShownThisSession();
    if (adsShown >= sessionCap) {
      return false;
    }

    // Increment translation count
    const currentCount = getTranslationCount();
    const newCount = currentCount + 1;
    localStorage.setItem('passportTalk_translationCount', String(newCount));

    // Check if we should show an ad
    if (newCount > 0 && newCount % triggerCount === 0) {
      // DOUBLE CHECK: Never show ads to premium users
      if (isPremiumUser) {
        console.log('[AdManager] Ad blocked at trigger - premium user');
        return false;
      }
      setShowAd(true);
      return true;
    }

    return false;
  }, [adsEnabled, adsMode, triggerCount, sessionCap, getTranslationCount, getAdsShownThisSession, isPremiumUser]);

  // Dismiss the ad
  const dismissAd = useCallback(() => {
    setShowAd(false);
    
    // Increment session ad count
    const adsShown = getAdsShownThisSession();
    sessionStorage.setItem('passportTalk_adsShown', String(adsShown + 1));
  }, [getAdsShownThisSession]);

  // Check if ads are available (not capped and not premium)
  const canShowAd = useCallback(() => {
    // CRITICAL: Premium users NEVER see ads
    if (isPremiumUser) {
      return false;
    }
    
    if (!adsEnabled || adsMode !== 'house') {
      return false;
    }
    const adsShown = getAdsShownThisSession();
    return adsShown < sessionCap;
  }, [adsEnabled, adsMode, sessionCap, getAdsShownThisSession, isPremiumUser]);

  // Get stats for debugging/admin
  const getStats = useCallback(() => {
    return {
      adsEnabled,
      adsMode,
      triggerCount,
      sessionCap,
      translationCount: getTranslationCount(),
      adsShownThisSession: getAdsShownThisSession(),
      canShowMore: canShowAd(),
      isVipUser,
      isAdmin,
      isInternalTester,
      isPremiumUser,
      subscriptionChecked,
    };
  }, [adsEnabled, adsMode, triggerCount, sessionCap, getTranslationCount, getAdsShownThisSession, canShowAd, isVipUser, isAdmin, isInternalTester, isPremiumUser, subscriptionChecked]);

  // Refresh user status - CALL THIS AFTER PAYMENT SUCCESS
  const refreshSubscription = useCallback(async () => {
    try {
      // Force refresh from backend - NO CACHING
      const response = await api.get('/user/profile');
      const data = response.data;
      
      setIsVipUser(data.is_vip === true);
      setIsAdmin(data.is_admin === true);
      setIsInternalTester(data.is_internal_tester === true);
      
      console.log('[AdManager] User status refreshed:', {
        is_vip: data.is_vip,
        is_admin: data.is_admin,
        is_internal_tester: data.is_internal_tester
      });
      
      // If user is now premium, immediately hide any showing ad
      if (data.is_vip || data.is_admin || data.is_internal_tester) {
        setShowAd(false);
      }
      
      return data;
    } catch (error) {
      console.error('[AdManager] Failed to refresh user status:', error);
      return null;
    }
  }, []);

  return {
    showAd: showAd && !isPremiumUser, // FINAL GUARD: Never return true for premium users
    recordTranslation,
    dismissAd,
    canShowAd,
    getStats,
    adsEnabled,
    isVipUser,
    isAdmin,
    isInternalTester,
    isPremiumUser,
    subscriptionChecked,
    refreshSubscription,
    // Legacy compatibility
    isProUser: isPremiumUser,
  };
};

export default useAdManager;
